<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
    <!-- Overlay -->
    <div class="absolute inset-0" @click="$emit('cancelModal')"></div>
    <!-- Modal Container -->
    <div class="relative w-full max-w-lg sm:max-w-2xl h-screen overflow-hidden bg-gray-800 rounded-xl shadow-2xl text-white flex flex-col">
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-700">
        <h2 id="modal-title" class="text-xl font-semibold text-indigo-300">แก้ไขแผนที่: {{ editedMap.name || 'ไม่มีชื่อ' }}</h2>
        <button @click="$emit('cancelModal')" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <!-- Tab Navigation -->
      <div class="flex border-b border-gray-700 bg-gray-800">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="switchTab(tab.id)"
          class="flex-1 px-4 py-3 text-sm font-medium text-white transition duration-200"
          :class="{
            'bg-indigo-600 border-b-2 border-indigo-500': currentTab === tab.id,
            'bg-gray-600 hover:bg-gray-500': currentTab !== tab.id
          }"
        >
          {{ tab.name }}
        </button>
      </div>
      <!-- Tab Content -->
      <div class="flex-1 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-indigo-500 scrollbar-track-gray-700">
        <form @submit.prevent="saveChanges">
          <!-- Basic Info Tab -->
          <div v-if="currentTab === 'basic'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 bg-gray-800 py-2">ข้อมูลพื้นฐาน</h3>
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ประเภทแผนที่ <span class="text-red-500">*</span></label>
                <input
                  v-model="editedMap.type"
                  type="text"
                  readonly
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 text-white cursor-not-allowed"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ชื่อแผนที่ <span class="text-red-500">*</span></label>
                <input
                  v-model="editedMap.name"
                  type="text"
                  placeholder="เช่น ป่ามืด"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ลิงก์รูปภาพ <span class="text-red-500">*</span></label>
                <input
                  v-model="editedMap.imageUrl"
                  type="url"
                  placeholder="https://example.com/map.jpg"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
                <div v-if="editedMap.imageUrl" class="mt-2">
                  <img :src="editedMap.imageUrl" alt="Map Preview" class="w-24 h-24 object-cover rounded-lg" @error="editedMap.imageUrl = ''" />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เลเวลที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.requiredLevel"
                  type="number"
                  min="1"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนห้องที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.roomRequired"
                  type="number"
                  min="1"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">พลังงานที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.requiredEnergy"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ลดความสุข <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.reduceHappy"
                  type="number"
                  min="0"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ชื่อเสียงที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.requiredRenown"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนที่สามารถหนีโดยใช้เหรียญได้ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.escapeAttempts"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนเหรียญที่ใช้สำหรับหนี <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.escapeCoins"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <h4 class="text-md font-medium text-gray-300 mt-4">ค่าเข้า</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">เหรียญ <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedMap.requiredCoins"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">ทอง <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedMap.requiredGold"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">เพชร <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedMap.requiredDiamonds"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">แคช <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedMap.requiredCash"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">แผนที่ที่ต้องมีก่อนหน้า</label>
                <input
                  v-model="editedMap.requiredMap"
                  type="text"
                  placeholder="เช่น บ้านร้าง (เว้นว่างได้)"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ตึกที่อนุญาตให้สำรวจ <span class="text-gray-500 text-xs font-normal">(ไม่เลือก = ทุกตึก)</span></label>
                <div class="flex items-center gap-4 mt-2">
                  <label class="inline-flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      :value="1"
                      v-model="editedMap.allowedBuildings"
                      class="form-checkbox h-5 w-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span class="text-sm text-gray-300">ตึก 1</span>
                  </label>
                  <label class="inline-flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      :value="2"
                      v-model="editedMap.allowedBuildings"
                      class="form-checkbox h-5 w-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span class="text-sm text-gray-300">ตึก 2</span>
                  </label>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">สถานะแผนที่ <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ editedMap.isActive ? 'ใช้งาน' : 'ไม่ใช้งาน' }}</span>
                  <button
                    type="button"
                    @click="toggleActive"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="editedMap.isActive ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะแผนที่"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="editedMap.isActive ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เปิดใช้งานการเจอพ่อค้าเร่ <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ editedMap.isMerchantEnabled ? 'ใช้งาน' : 'ไม่ใช้งาน' }}</span>
                  <button
                    type="button"
                    @click="toggleMerchantEnabled"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="editedMap.isMerchantEnabled ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะพ่อค้าเร่"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="editedMap.isMerchantEnabled ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เปิดใช้งานการเจอชาวบ้าน <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ editedMap.isVillagerEnabled ? 'ใช้งาน' : 'ไม่ใช้งาน' }}</span>
                  <button
                    type="button"
                    @click="toggleVillagerEnabled"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="editedMap.isVillagerEnabled ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะชาวบ้าน"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="editedMap.isVillagerEnabled ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เปิดใช้งานการเจอสิ่งมีชีวิตกลายพันธุ์ <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ editedMap.isMutantEnabled ? 'ใช้งาน' : 'ไม่ใช้งาน' }}</span>
                  <button
                    type="button"
                    @click="toggleMutantEnabled"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="editedMap.isMutantEnabled ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะสิ่งมีชีวิตกลายพันธุ์"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="editedMap.isMutantEnabled ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">อนุญาตให้ยึดครองแผนที่ <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ editedMap.isOccupationAllowed ? 'อนุญาต' : 'ไม่อนุญาต' }}</span>
                  <button
                    type="button"
                    @click="toggleOccupationAllowed"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="editedMap.isOccupationAllowed ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะการยึดครอง"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="editedMap.isOccupationAllowed ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">อนุญาตให้ปล้น <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ editedMap.isLootingAllowed ? 'อนุญาต' : 'ไม่อนุญาต' }}</span>
                  <button
                    type="button"
                    @click="toggleLootingAllowed"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="editedMap.isLootingAllowed ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะการปล้น"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="editedMap.isLootingAllowed ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">UID ผู้ยึดครองแผนที่ปัจจุบัน</label>
                <input
                  v-model="editedMap.occupierUid"
                  type="text"
                  placeholder="UID ของผู้เล่น (เว้นว่างหากไม่มี)"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">คะแนนยึดครองแผนที่ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.occupationScore"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ค่าใช้จ่ายในการท้าประลอง (เพชร) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.challengeCostDiamonds"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">รางวัลรายวันสำหรับผู้ยึดครอง (เพชร) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.dailyDiamondReward"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสเจอผู้ยึดครอง (%) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.encounterChance"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
            </div>
          </div>
          <!-- Zombies Tab -->
          <div v-if="currentTab === 'zombies'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-200 bg-gray-800 py-2">ซอมบี้ในแผนที่</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนซอมบี้ขั้นต่ำ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.minZombies"
                  type="number"
                  min="1"
                  placeholder="5"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนซอมบี้สูงสุด <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.maxZombies"
                  type="number"
                  :min="editedMap.minZombies"
                  placeholder="10"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
                <p v-if="editedMap.maxZombies < editedMap.minZombies" class="text-red-500 text-sm mt-1">ต้องมากกว่าหรือเท่ากับขั้นต่ำ</p>
              </div>
            </div>
            <div class="flex gap-3 mb-3">
              <button
                @click="toggleAllZombies"
                type="button"
                class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
              >
                {{ zombieShowDetailsAll ? 'ไม่แสดงทั้งหมด' : 'แสดงทั้งหมด' }}
              </button>
            </div>
            <div class="space-y-3">
              <div
                v-for="(zombie, index) in editedMap.zombies"
                :key="index"
                class="p-4 bg-gray-900 bg-opacity-50 rounded-lg transition-all duration-200 animate-fade-in"
              >
                <div v-if="zombieLoadingStates[index]" class="flex justify-center items-center py-4">
                  <div class="loader"></div>
                </div>
                <div v-else-if="zombieShowDetailsAll && zombieDetails[zombie.name]" class="mb-4 text-gray-300 text-sm bg-glass p-4 rounded-lg flex items-start gap-4">
                  <img
                    v-if="zombieDetails[zombie.name]?.imageUrl"
                    :src="zombieDetails[zombie.name].imageUrl"
                    :alt="zombie.name"
                    class="w-10 h-10 object-cover rounded-full border border-gray-600"
                    @error="handleImageError"
                  />
                  <div class="space-y-2">
                    <div>
                      <span class="font-bold">ประเภท:</span> {{ zombieDetails[zombie.name]?.type || 'N/A' }}
                    </div>
                    <div>
                      <span class="font-bold">เลเวล:</span> {{ zombieDetails[zombie.name]?.level || 0 }}
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-12 items-center gap-3">
                  <div class="col-span-6 sm:col-span-7">
                    <input
                      v-model="zombie.name"
                      type="text"
                      placeholder="ชื่อซอมบี้ (เช่น Zombie Basic)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-4 sm:col-span-3">
                    <input
                      v-model.number="zombie.chance"
                      type="number"
                      min="0"
                      max="100"
                      placeholder="%"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-2">
                    <button
                      v-if="index > 0"
                      @click="removeZombie(index)"
                      type="button"
                      class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบซอมบี้"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addZombie"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มซอมบี้
                </button>
                <button
                  @click="normalizeChances"
                  type="button"
                  class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
                >
                  คำนวณโอกาส
                </button>
              </div>
              <div v-if="totalChance > 0" class="text-sm text-gray-400 mt-2">
                <p>โอกาสการเจอซอมบี้ (รวม: {{ totalChance.toFixed(2) }}%):</p>
                <p v-if="remainingChance > 0" class="text-yellow-500">เหลือ: {{ remainingChance.toFixed(2) }}%</p>
                <p v-else-if="remainingChance < 0" class="text-red-500">เกิน: {{ Math.abs(remainingChance).toFixed(2) }}%</p>
                <ul class="list-disc list-inside">
                  <li v-for="(zombie, index) in editedMap.zombies" :key="index">
                    {{ zombie.name || 'ยังไม่ระบุ' }}: {{ zombie.chance || 0 }}%
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <!-- Merchants Tab -->
          <div v-if="currentTab === 'merchants'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-200 bg-gray-800 py-2">พ่อค้าเร่ในแผนที่</h3>
            <div class="flex gap-3 mb-3">
              <button
                @click="toggleAllMerchants"
                type="button"
                class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
              >
                {{ merchantShowDetailsAll ? 'ไม่แสดงทั้งหมด' : 'แสดงทั้งหมด' }}
              </button>
              <button
                @click="sortMerchants"
                type="button"
                class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
              >
                จัดเรียงพ่อค้า
              </button>
            </div>
            <div class="space-y-3">
              <div
                v-for="(merchant, index) in editedMap.merchants"
                :key="index"
                class="p-4 bg-gray-900 bg-opacity-50 rounded-lg transition-all duration-200 animate-fade-in"
              >
                <div v-if="merchantLoadingStates[index]" class="flex justify-center items-center py-4">
                  <div class="loader"></div>
                </div>
                <div v-else-if="merchantShowDetailsAll && merchantDetails[merchant.name]" class="mb-4 text-gray-300 text-sm bg-glass p-4 rounded-lg flex items-start gap-4">
                  <img
                    v-if="merchantDetails[merchant.name]?.img"
                    :src="merchantDetails[merchant.name].img"
                    :alt="merchant.name"
                    class="w-10 h-10 object-cover rounded-full border border-gray-600"
                    @error="handleImageError"
                  />
                  <div class="space-y-2">
                    <div>
                      <span class="font-bold">ระดับความหายาก:</span> {{ getStars(merchantDetails[merchant.name]?.rarity) }}
                    </div>
                    <div>
                      <span class="font-bold">สินค้าที่ขาย:</span>
                      <ul class="list-disc list-inside ml-4">
                        <li v-for="(item, itemIndex) in merchantDetails[merchant.name].items" :key="itemIndex">
                          {{ item.name }} - {{ item.price }} {{ item.currency }}
                        </li>
                      </ul>
                    </div>
                    <div>
                      <span class="font-bold">อนุญาตให้ต่อสู้เพื่อปล้น:</span> {{ merchantDetails[merchant.name]?.allowRob ? 'ใช่' : 'ไม่' }}
                    </div>
                    <div v-if="merchantDetails[merchant.name]?.allowRob">
                      <span class="font-bold">รางวัลจากการปล้น:</span>
                      <ul class="list-disc list-inside ml-4">
                        <li>Silver: {{ merchantDetails[merchant.name].robRewards.silver }}</li>
                        <li>Gold: {{ merchantDetails[merchant.name].robRewards.gold }}</li>
                        <li>Diamond: {{ merchantDetails[merchant.name].robRewards.diamond }}</li>
                      </ul>
                    </div>
                    <div>
                      <span class="font-bold">รูปหน้าปกติ:</span>
                      <img v-if="merchantDetails[merchant.name]?.normalFace" :src="merchantDetails[merchant.name].normalFace" alt="Normal Face" class="w-8 h-8 object-cover rounded-full inline-block ml-2" @error="handleImageError" />
                    </div>
                    <div>
                      <span class="font-bold">รูปหน้าที่โมโห:</span>
                      <img v-if="merchantDetails[merchant.name]?.angryFace" :src="merchantDetails[merchant.name].angryFace" alt="Angry Face" class="w-8 h-8 object-cover rounded-full inline-block ml-2" @error="handleImageError" />
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-12 items-center gap-3">
                  <div class="col-span-6 sm:col-span-7">
                    <input
                      v-model="merchant.name"
                      type="text"
                      placeholder="ชื่อพ่อค้าเร่ (เช่น Merchant Basic)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-4 sm:col-span-3">
                    <input
                      v-model.number="merchant.chance"
                      type="number"
                      min="1"
                      max="1000"
                      placeholder="1-1000 (1000=10%)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-2">
                    <button
                      v-if="index > 0"
                      @click="removeMerchant(index)"
                      type="button"
                      class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบพ่อค้าเร่"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addMerchant"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มพ่อค้าเร่
                </button>
              </div>
            </div>
          </div>
          <!-- Mutants Tab -->
          <div v-if="currentTab === 'mutants'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-200 bg-gray-800 py-2">สิ่งมีชีวิตกลายพันธุ์ในแผนที่</h3>
            <div class="flex gap-3 mb-3">
              <button
                @click="toggleAllMutants"
                type="button"
                class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
              >
                {{ mutantShowDetailsAll ? 'ไม่แสดงทั้งหมด' : 'แสดงทั้งหมด' }}
              </button>
              <button
                @click="sortMutants"
                type="button"
                class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
              >
                จัดเรียงสิ่งมีชีวิต
              </button>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">คุณต้องการให้ค่ารวมออกมาที่เท่าไหร่</label>
                <input
                  v-model.number="totalDesiredMutantChance"
                  type="number"
                  min="0"
                  placeholder="เช่น 1000"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                />
              </div>
              <div class="flex items-end">
                <button
                  @click="calculateMutantChances"
                  type="button"
                  class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
                >
                  ประมวลผล
                </button>
              </div>
            </div>
            <div class="space-y-3">
              <div
                v-for="(mutant, index) in editedMap.mutants"
                :key="index"
                class="p-4 bg-gray-900 bg-opacity-50 rounded-lg transition-all duration-200 animate-fade-in"
              >
                <div v-if="mutantLoadingStates[index]" class="flex justify-center items-center py-4">
                  <div class="loader"></div>
                </div>
                <div v-else-if="mutantShowDetailsAll && mutantDetails[mutant.name]" class="mb-4 text-gray-300 text-sm bg-glass p-4 rounded-lg flex items-start gap-4">
                  <img
                    v-if="mutantDetails[mutant.name]?.img"
                    :src="mutantDetails[mutant.name].img"
                    :alt="mutant.name"
                    class="w-10 h-10 object-cover rounded-full border border-gray-600"
                    @error="handleImageError"
                  />
                  <div class="space-y-2">
                    <div>
                      <span class="font-bold">ระดับความหายาก:</span> {{ getStars(mutantDetails[mutant.name]?.rarity) }}
                    </div>
                    <div>
                      <span class="font-bold">ค่าสถานะ:</span> Sta{{ mutantDetails[mutant.name]?.stats?.stamina || 0 }} Str{{ mutantDetails[mutant.name]?.stats?.str || 0 }} Int{{ mutantDetails[mutant.name]?.stats?.int || 0 }} Agi{{ mutantDetails[mutant.name]?.stats?.agi || 0 }} Def{{ mutantDetails[mutant.name]?.stats?.defense || 0 }} Luck{{ mutantDetails[mutant.name]?.stats?.luck || 0 }}
                    </div>
                    <div>
                      <span class="font-bold">สามารถจับได้:</span> {{ mutantDetails[mutant.name]?.canCatch ? 'ใช่' : 'ไม่' }}
                    </div>
                    <div v-if="mutantDetails[mutant.name]?.canCatch">
                      <span class="font-bold">ระดับความยาก:</span> {{ mutantDetails[mutant.name]?.catchDifficulty || 1 }}
                    </div>
                    <div>
                      <span class="font-bold">รูปหน้าปกติ:</span>
                      <img v-if="mutantDetails[mutant.name]?.normalFace" :src="mutantDetails[mutant.name].normalFace" alt="Normal Face" class="w-8 h-8 object-cover rounded-full inline-block ml-2" @error="handleImageError" />
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-12 items-center gap-3">
                  <div class="col-span-6 sm:col-span-7">
                    <input
                      v-model="mutant.name"
                      type="text"
                      placeholder="ชื่อสิ่งมีชีวิตกลายพันธุ์ (เช่น Mutant Basic)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-4 sm:col-span-3">
                    <input
                      v-model.number="mutant.chance"
                      type="number"
                      min="1"
                      max="1000"
                      placeholder="1-1000 (1000=10%)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-2">
                    <button
                      v-if="index > 0"
                      @click="removeMutant(index)"
                      type="button"
                      class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบสิ่งมีชีวิตกลายพันธุ์"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addMutant"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มสิ่งมีชีวิตกลายพันธุ์
                </button>
              </div>
            </div>
          </div>
          <!-- Villagers Tab -->
          <div v-if="currentTab === 'villagers'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-200 bg-gray-800 py-2">ชาวบ้านในแผนที่</h3>
            <div class="flex gap-3 mb-3">
              <button
                @click="toggleAllVillagers"
                type="button"
                class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
              >
                {{ villagerShowDetailsAll ? 'ไม่แสดงทั้งหมด' : 'แสดงทั้งหมด' }}
              </button>
              <button
                @click="sortVillagers"
                type="button"
                class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
              >
                จัดเรียงชาวบ้าน
              </button>
            </div>
            <div class="space-y-3">
              <div
                v-for="(villager, index) in editedMap.villagers"
                :key="index"
                class="p-4 bg-gray-900 bg-opacity-50 rounded-lg transition-all duration-200 animate-fade-in"
              >
                <div v-if="villagerLoadingStates[index]" class="flex justify-center items-center py-4">
                  <div class="loader"></div>
                </div>
                <div v-else-if="villagerShowDetailsAll && villagerDetails[villager.name]" class="mb-4 text-gray-300 text-sm bg-glass p-4 rounded-lg flex items-start gap-4">
                  <img
                    v-if="villagerDetails[villager.name]?.img"
                    :src="villagerDetails[villager.name].img"
                    :alt="villager.name"
                    class="w-10 h-10 object-cover rounded-full border border-gray-600"
                    @error="handleImageError"
                  />
                  <div class="space-y-2">
                    <div>
                      <span class="font-bold">เลเวล:</span> {{ getStars(villagerDetails[villager.name]?.level) }}
                    </div>
                    <div>
                      <span class="font-bold">ค่าใช้จ่ายในการจ้าง:</span>
                      <ul class="list-disc list-inside ml-4">
                        <li>Silver: {{ villagerDetails[villager.name].hireCosts.silver }}</li>
                        <li>Gold: {{ villagerDetails[villager.name].hireCosts.gold }}</li>
                        <li>Diamond: {{ villagerDetails[villager.name].hireCosts.diamond }}</li>
                      </ul>
                    </div>
                    <div>
                      <span class="font-bold">ค่าสถานะ:</span> Sta{{ villagerDetails[villager.name]?.stats?.stamina || 0 }} Str{{ villagerDetails[villager.name]?.stats?.str || 0 }} Int{{ villagerDetails[villager.name]?.stats?.int || 0 }} Agi{{ villagerDetails[villager.name]?.stats?.agi || 0 }} Def{{ villagerDetails[villager.name]?.stats?.defense || 0 }} Luck{{ villagerDetails[villager.name]?.stats?.luck || 0 }}
                    </div>
                    <div>
                      <span class="font-bold">จำนวนลูกเต๋า:</span> บุก{{ villagerDetails[villager.name]?.attackDice || 0 }} ตั้งรับ{{ villagerDetails[villager.name]?.defenseDice || 0 }}
                    </div>
                    <div>
                      <span class="font-bold">รูปหน้าปกติ:</span>
                      <img v-if="villagerDetails[villager.name]?.normalFace" :src="villagerDetails[villager.name].normalFace" alt="Normal Face" class="w-8 h-8 object-cover rounded-full inline-block ml-2" @error="handleImageError" />
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-12 items-center gap-3">
                  <div class="col-span-6 sm:col-span-7">
                    <input
                      v-model="villager.name"
                      type="text"
                      placeholder="ชื่อชาวบ้าน (เช่น Villager Basic)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-4 sm:col-span-3">
                    <input
                      v-model.number="villager.chance"
                      type="number"
                      min="1"
                      max="10000"
                      placeholder="1-10000"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-2">
                    <button
                      v-if="index > 0"
                      @click="removeVillager(index)"
                      type="button"
                      class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบชาวบ้าน"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addVillager"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มชาวบ้าน
                </button>
              </div>
            </div>
          </div>
          <!-- Rewards Tab -->
          <div v-if="currentTab === 'rewards'" class="space-y-4 animate-fadeIn">
            <h3 class="text-lg font-medium text-gray-200 bg-gray-800 py-2">รางวัลเมื่อสำเร็จ</h3>
            <h4 class="text-md font-medium text-gray-300 mt-4">รางวัลเพชร</h4>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนเพชรขั้นต่ำ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.diamondDropMin"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนเพชรสูงสุด <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.diamondDropMax"
                  type="number"
                  :min="editedMap.diamondDropMin"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
                <p v-if="editedMap.diamondDropMax < editedMap.diamondDropMin" class="text-red-500 text-sm mt-1">ต้องมากกว่าหรือเท่ากับขั้นต่ำ</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสได้รับเพชร (%) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedMap.diamondDropChance"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
            </div>
            <div class="flex items-center gap-2 mb-3">
              <label for="itemTypeFilter" class="text-gray-300 text-sm font-medium">ประเภทไอเทม:</label>
              <select
                id="itemTypeFilter"
                v-model="selectedItemType"
                class="bg-gray-700 text-white border border-gray-600 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">ทั้งหมด</option>
                <option value="wearable">ไอเทมสวมใส่</option>
                <option value="book">หนังสือ</option>
                <option value="general">ทั่วไป</option>
                <option value="food">อาหาร</option>
                <option value="map">แผนที่</option>
                <option value="heal">รักษา</option>
                <option value="loot">ปล้น</option>
              </select>
            </div>
            <div class="flex gap-3 mb-3">
              <button
                @click="toggleAllRewards"
                type="button"
                class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
              >
                {{ rewardShowDetailsAll ? 'ไม่แสดงทั้งหมด' : 'แสดงทั้งหมด' }}
              </button>
            </div>
            <div class="bg-gray-900 bg-opacity-50 p-4 rounded-lg text-sm text-gray-300">
              <p><span class="font-bold">จำนวนไอเทมประเภทสวมใส่:</span> {{ wearableItemsCount }} ชิ้น</p>
              <p><span class="font-bold">จำนวนไอเทมประเภททั่วไป:</span> {{ generalItemsCount }} ชิ้น</p>
              <p><span class="font-bold">จำนวนรางวัลทั้งหมด:</span> {{ filteredRewards.length }} ชิ้น</p>
            </div>
            <div class="space-y-3">
              <div
                v-for="(reward, index) in filteredRewards"
                :key="index"
                class="p-4 bg-gray-900 bg-opacity-50 rounded-lg transition-all duration-200 animate-fade-in"
                :class="itemDetails[reward.itemName]?.rarityColors?.borderColor"
              >
                <div v-if="loadingStates[index]" class="flex justify-center items-center py-4">
                  <div class="loader"></div>
                </div>
                <div v-else-if="rewardShowDetailsAll && itemDetails[reward.itemName]" class="mb-4 text-gray-300 text-sm bg-glass p-4 rounded-lg flex items-start gap-4">
                  <img
                    v-if="itemDetails[reward.itemName]?.img"
                    :src="itemDetails[reward.itemName].img"
                    :alt="reward.itemName"
                    class="w-10 h-10 object-cover rounded-full border border-gray-600"
                    @error="handleImageError"
                  />
                  <div class="space-y-2">
                    <div>
                      <span class="font-bold">ประเภท:</span> {{ itemDetails[reward.itemName]?.type || 'N/A' }}
                      <span>(ราคา: {{ itemDetails[reward.itemName]?.price || 0 }})</span>
                    </div>
                    <div>
                      <span class="font-bold">ความหายาก:</span> {{ getStars(itemDetails[reward.itemName]?.rarity) }}
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-12 items-center gap-3">
                  <div class="col-span-6 sm:col-span-7">
                    <input
                      v-model="reward.itemName"
                      type="text"
                      placeholder="ชื่ออุปกรณ์ (เช่น กระสุน)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-4 sm:col-span-3">
                    <input
                      v-model.number="reward.dropChance"
                      type="number"
                      min="0"
                      max="10000"
                      placeholder="จาก 10,000"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-2">
                    <button
                      @click="removeReward(index)"
                      type="button"
                      class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบรางวัล"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mb-3">
                <button
                  @click="addReward"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มรางวัล
                </button>
                <button
                  @click="sortRewards"
                  type="button"
                  class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
                >
                  จัดเรียงรางวัล
                </button>
              </div>
            </div>
            <p v-if="filteredRewards.length === 0" class="text-gray-300 text-sm">ไม่มีรางวัลที่ตรงกับตัวกรอง</p>
          </div>
          <!-- Situations Tab -->
          <div v-if="currentTab === 'situations'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-200 bg-gray-800 py-2">สถานการณ์ในแผนที่</h3>
            <div class="space-y-3">
              <div
                v-for="(situation, situationIndex) in editedMap.situations"
                :key="situationIndex"
                class="p-4 bg-gray-900 bg-opacity-50 rounded-lg transition-all duration-200 animate-fade-in"
              >
                <div class="space-y-4">
                  <div class="grid grid-cols-1 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1">ชื่อสถานการณ์ <span class="text-red-500">*</span></label>
                      <input
                        v-model="situation.name"
                        type="text"
                        placeholder="เช่น ฝูงซอมบี้บุก"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนซอมบี้ขั้นต่ำ <span class="text-red-500">*</span></label>
                        <input
                          v-model.number="situation.minZombies"
                          type="number"
                          min="1"
                          placeholder="5"
                          class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                          required
                          aria-required="true"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนซอมบี้สูงสุด <span class="text-red-500">*</span></label>
                        <input
                          v-model.number="situation.maxZombies"
                          type="number"
                          :min="situation.minZombies"
                          placeholder="10"
                          class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                          required
                          aria-required="true"
                        />
                        <p v-if="situation.maxZombies < situation.minZombies" class="text-red-500 text-sm mt-1">ต้องมากกว่าหรือเท่ากับขั้นต่ำ</p>
                      </div>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสเกิด (%) <span class="text-red-500">*</span></label>
                      <input
                        v-model.number="situation.chance"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="10"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>
                  <div class="mt-4">
                    <h4 class="text-md font-medium text-gray-300 mb-2">รางวัลของสถานการณ์</h4>
                    <div class="space-y-3">
                      <div
                        v-for="(reward, rewardIndex) in situation.rewards"
                        :key="rewardIndex"
                        class="p-3 bg-gray-800 bg-opacity-50 rounded-lg"
                      >
                        <div class="grid grid-cols-12 items-center gap-3">
                          <div class="col-span-6 sm:col-span-7">
                            <input
                              v-model="reward.itemName"
                              type="text"
                              placeholder="ชื่อรางวัล (เช่น กระสุน)"
                              class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                            />
                          </div>
                          <div class="col-span-4 sm:col-span-3">
                            <input
                              v-model.number="reward.dropChance"
                              type="number"
                              min="0"
                              max="10000"
                              placeholder="จาก 10,000"
                              class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                            />
                          </div>
                          <div class="col-span-2">
                            <button
                              @click="removeSituationReward(situationIndex, rewardIndex)"
                              type="button"
                              class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                              aria-label="ลบรางวัล"
                            >
                              ลบ
                            </button>
                          </div>
                        </div>
                      </div>
                      <button
                        @click="addSituationReward(situationIndex)"
                        type="button"
                        class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                      >
                        + เพิ่มรางวัล
                      </button>
                    </div>
                  </div>
                  <div class="flex justify-end mt-3">
                    <button
                      v-if="situationIndex > 0"
                      @click="removeSituation(situationIndex)"
                      type="button"
                      class="px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบสถานการณ์"
                    >
                      ลบสถานการณ์
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addSituation"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มสถานการณ์
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      <!-- Back to Top Button -->
      <button
        v-if="showBackToTop"
        @click="scrollToTop"
        class="fixed bottom-20 right-6 px-4 py-2 bg-indigo-600 rounded-lg text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-200"
        aria-label="กลับไปด้านบน"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
        </svg>
      </button>
      <!-- Fixed Action Buttons -->
      <div class="flex justify-between gap-3 p-6 border-t border-gray-700 bg-gray-800">
        <button
          @click="deleteMap"
          type="button"
          class="px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
        >
          ลบแผนที่
        </button>
        <div class="flex gap-3">
          <button
            @click="$emit('cancelModal')"
            type="button"
            class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-200"
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            @click="saveChanges"
            class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
            :disabled="totalChance > 100 || editedMap.maxZombies < editedMap.minZombies || hasInvalidSituations"
          >
            บันทึก
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { projectFirestore } from '@/firebase/config';
import gameItemsData from '@/data/gameItems.json';
export default {
  props: { map: { type: Object, required: true } },
  emits: ['cancelModal', 'mapUpdated', 'mapDeleted'],
  setup(props, { emit }) {
    const editedMap = ref({
      ...props.map,
      type: props.map.type || 'สถานที่',
      requiredEnergy: props.map.requiredEnergy || 0,
      roomRequired: props.map.roomRequired || 1,
      minZombies: props.map.minZombies || 5,
      maxZombies: props.map.maxZombies || 10,
      requiredCoins: props.map.requiredCoins || 0,
      requiredGold: props.map.requiredGold || 0,
      requiredDiamonds: props.map.requiredDiamonds || 0,
      requiredCash: props.map.requiredCash || 0,
      requiredRenown: props.map.requiredRenown || 0,
      requiredMap: props.map.requiredMap || '',
      allowedBuildings: Array.isArray(props.map.allowedBuildings) ? [...props.map.allowedBuildings] : [],
      escapeAttempts: props.map.escapeAttempts || 0,
      escapeCoins: props.map.escapeCoins || 0,
      isActive: props.map.isActive !== undefined ? props.map.isActive : true,
      isMerchantEnabled: props.map.isMerchantEnabled !== undefined ? props.map.isMerchantEnabled : true,
      isVillagerEnabled: props.map.isVillagerEnabled !== undefined ? props.map.isVillagerEnabled : true,
      isMutantEnabled: props.map.isMutantEnabled !== undefined ? props.map.isMutantEnabled : true,
      isLootingAllowed: props.map.isLootingAllowed !== undefined ? props.map.isLootingAllowed : false,
      zombies: props.map.zombies ? [...props.map.zombies] : [{ name: '', chance: 0 }],
      merchants: props.map.merchants ? [...props.map.merchants] : [{ name: '', chance: 0 }],
      mutants: props.map.mutants ? [...props.map.mutants] : [{ name: '', chance: 0 }],
      villagers: props.map.villagers ? [...props.map.villagers] : [{ name: '', chance: 0 }],
      rewards: props.map.rewards ? [...props.map.rewards] : [{ itemName: '', dropChance: 0 }],
      situations: props.map.situations ? [...props.map.situations] : [{ name: '', minZombies: 1, maxZombies: 1, chance: 0, rewards: [{ itemName: '', dropChance: 0 }] }],
      diamondDropMin: props.map.diamondDropMin || 0,
      diamondDropMax: props.map.diamondDropMax || 0,
      diamondDropChance: props.map.diamondDropChance || 0,
      isOccupationAllowed: props.map.isOccupationAllowed !== undefined ? props.map.isOccupationAllowed : false,
      occupierUid: props.map.occupierUid || '',
      occupationScore: props.map.occupationScore || 0,
      challengeCostDiamonds: props.map.challengeCostDiamonds || 0,
      dailyDiamondReward: props.map.dailyDiamondReward || 0,
      encounterChance: props.map.encounterChance || 1,
    });
    const totalChance = computed(() => (editedMap.value.zombies || []).reduce((sum, z) => sum + (z.chance || 0), 0));
    const remainingChance = computed(() => 100 - totalChance.value);
    const hasInvalidSituations = computed(() => {
      return (editedMap.value.situations || []).some(s => s.maxZombies < s.minZombies);
    });
    const wearableItemTypes = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง', 'สนับแข้ง',
      'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง', 'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์'
    ];
    const generalItemTypes = ['ทั่วไป', 'อาหาร', 'แผนที่', 'หนังสือ', 'รักษา', 'ปล้น'];
    const selectedItemType = ref('');
    const filteredRewards = computed(() => {
      const rewards = editedMap.value.rewards || [];
      if (!selectedItemType.value) return rewards;
      return rewards.filter(reward => {
        const item = itemDetails.value[reward.itemName];
        if (!item) return false;
        if (selectedItemType.value === 'wearable') return wearableItemTypes.includes(item.type);
        if (selectedItemType.value === 'book') return item.type === 'หนังสือ';
        if (selectedItemType.value === 'general') return item.type === 'ทั่วไป';
        if (selectedItemType.value === 'food') return item.type === 'อาหาร';
        if (selectedItemType.value === 'map') return item.type === 'แผนที่';
        if (selectedItemType.value === 'heal') return item.type === 'รักษา';
        if (selectedItemType.value === 'loot') return item.type === 'ปล้น';
        return false;
      });
    });
    const wearableItemsCount = computed(() => {
      return filteredRewards.value.filter((reward) => {
        const item = itemDetails.value[reward.itemName];
        return item && wearableItemTypes.includes(item.type);
      }).length;
    });
    const generalItemsCount = computed(() => {
      return filteredRewards.value.filter((reward) => {
        const item = itemDetails.value[reward.itemName];
        return item && generalItemTypes.includes(item.type);
      }).length;
    });
    const showBackToTop = ref(false);
    const itemDetails = ref({});
    const loadingStates = ref({});
    const zombieDetails = ref({});
    const zombieLoadingStates = ref({});
    const zombieShowDetailsAll = ref(false);
    const merchantDetails = ref({});
    const merchantLoadingStates = ref({});
    const merchantShowDetailsAll = ref(false);
    const mutantDetails = ref({});
    const mutantLoadingStates = ref({});
    const mutantShowDetailsAll = ref(false);
    const villagerDetails = ref({});
    const villagerLoadingStates = ref({});
    const villagerShowDetailsAll = ref(false);
    const rewardShowDetailsAll = ref(false);
    const tabs = [
      { id: 'basic', name: 'ข้อมูลพื้นฐาน' },
      { id: 'zombies', name: 'ซอมบี้ในแผนที่' },
      { id: 'merchants', name: 'พ่อค้าเร่' },
      { id: 'mutants', name: 'สิ่งมีชีวิตกลายพันธุ์' },
      { id: 'villagers', name: 'ชาวบ้าน' },
      { id: 'rewards', name: 'รางวัล' },
      { id: 'situations', name: 'สถานการณ์' },
    ];
    const currentTab = ref('basic');
    const switchTab = (tabId) => {
      currentTab.value = tabId;
      selectedItemType.value = '';
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.scrollTo({ top: 0, behavior: 'smooth' });
      }
      showBackToTop.value = false;
    };
    const addZombie = () => {
      const index = editedMap.value.zombies.length;
      editedMap.value.zombies.push({ name: '', chance: 0 });
      zombieLoadingStates.value[index] = false;
    };
    const removeZombie = (index) => {
      if (editedMap.value.zombies.length > 1) {
        editedMap.value.zombies.splice(index, 1);
        delete zombieLoadingStates.value[index];
        const newZombieLoadingStates = {};
        Object.keys(zombieLoadingStates.value).forEach((key) => {
          const newKey = parseInt(key) > index ? parseInt(key) - 1 : parseInt(key);
          newZombieLoadingStates[newKey] = zombieLoadingStates.value[key];
        });
        zombieLoadingStates.value = newZombieLoadingStates;
      }
    };
    const toggleAllZombies = () => {
      zombieShowDetailsAll.value = !zombieShowDetailsAll.value;
      localStorage.setItem(`zombie_details_all_${props.map.id}`, zombieShowDetailsAll.value.toString());
    };
    const normalizeChances = () => {
      const currentTotal = totalChance.value;
      if (currentTotal === 0) {
        const baseChance = Math.floor(100 / editedMap.value.zombies.length);
        let remaining = 100 - baseChance * editedMap.value.zombies.length;
        editedMap.value.zombies.forEach((zombie, index) => {
          zombie.chance = baseChance + (index < remaining ? 1 : 0);
        });
      } else {
        const scaleFactor = 100 / currentTotal;
        const scaledChances = editedMap.value.zombies.map((zombie) => (zombie.chance || 0) * scaleFactor);
        const flooredChances = scaledChances.map((chance) => Math.floor(chance));
        const totalFloored = flooredChances.reduce((sum, chance) => sum + chance, 0);
        const remaining = 100 - totalFloored;
        const fractionalParts = scaledChances.map((chance, index) => ({
          index,
          fractional: chance - flooredChances[index],
        })).sort((a, b) => b.fractional - a.fractional);
        for (let i = 0; i < remaining; i++) {
          flooredChances[fractionalParts[i].index]++;
        }
        editedMap.value.zombies.forEach((zombie, index) => {
          zombie.chance = flooredChances[index];
        });
      }
    };
    const addMerchant = () => {
      const index = editedMap.value.merchants.length;
      editedMap.value.merchants.push({ name: '', chance: 0 });
      merchantLoadingStates.value[index] = false;
    };
    const removeMerchant = (index) => {
      if (editedMap.value.merchants.length > 1) {
        editedMap.value.merchants.splice(index, 1);
        delete merchantLoadingStates.value[index];
        const newMerchantLoadingStates = {};
        Object.keys(merchantLoadingStates.value).forEach((key) => {
          const newKey = parseInt(key) > index ? parseInt(key) - 1 : parseInt(key);
          newMerchantLoadingStates[newKey] = merchantLoadingStates.value[key];
        });
        merchantLoadingStates.value = newMerchantLoadingStates;
      }
    };
    const toggleAllMerchants = () => {
      merchantShowDetailsAll.value = !merchantShowDetailsAll.value;
      localStorage.setItem(`merchant_details_all_${props.map.id}`, merchantShowDetailsAll.value.toString());
    };
    const sortMerchants = () => {
      editedMap.value.merchants.sort((a, b) => (a.chance || 0) - (b.chance || 0));
      const newMerchantLoadingStates = {};
      editedMap.value.merchants.forEach((_, index) => {
        newMerchantLoadingStates[index] = merchantLoadingStates.value[index] || false;
      });
      merchantLoadingStates.value = newMerchantLoadingStates;
    };
    const totalDesiredMutantChance = ref(0);
    const addMutant = () => {
      const index = editedMap.value.mutants.length;
      editedMap.value.mutants.push({ name: '', chance: 0 });
      mutantLoadingStates.value[index] = false;
    };
    const removeMutant = (index) => {
      if (editedMap.value.mutants.length > 1) {
        editedMap.value.mutants.splice(index, 1);
        delete mutantLoadingStates.value[index];
        const newMutantLoadingStates = {};
        Object.keys(mutantLoadingStates.value).forEach((key) => {
          const newKey = parseInt(key) > index ? parseInt(key) - 1 : parseInt(key);
          newMutantLoadingStates[newKey] = mutantLoadingStates.value[key];
        });
        mutantLoadingStates.value = newMutantLoadingStates;
      }
    };
    const toggleAllMutants = () => {
      mutantShowDetailsAll.value = !mutantShowDetailsAll.value;
      localStorage.setItem(`mutant_details_all_${props.map.id}`, mutantShowDetailsAll.value.toString());
    };
    const sortMutants = () => {
      editedMap.value.mutants.sort((a, b) => (a.chance || 0) - (b.chance || 0));
      const newMutantLoadingStates = {};
      editedMap.value.mutants.forEach((_, index) => {
        newMutantLoadingStates[index] = mutantLoadingStates.value[index] || false;
      });
      mutantLoadingStates.value = newMutantLoadingStates;
    };
    const calculateMutantChances = () => {
      const numMutants = editedMap.value.mutants.length;
      if (numMutants > 0 && totalDesiredMutantChance.value > 0) {
        const equalChance = Math.floor(totalDesiredMutantChance.value / numMutants);
        editedMap.value.mutants.forEach((mutant) => {
          mutant.chance = equalChance;
        });
      }
    };
    const addVillager = () => {
      const index = editedMap.value.villagers.length;
      editedMap.value.villagers.push({ name: '', chance: 0 });
      villagerLoadingStates.value[index] = false;
    };
    const removeVillager = (index) => {
      if (editedMap.value.villagers.length > 1) {
        editedMap.value.villagers.splice(index, 1);
        delete villagerLoadingStates.value[index];
        const newVillagerLoadingStates = {};
        Object.keys(villagerLoadingStates.value).forEach((key) => {
          const newKey = parseInt(key) > index ? parseInt(key) - 1 : parseInt(key);
          newVillagerLoadingStates[newKey] = villagerLoadingStates.value[key];
        });
        villagerLoadingStates.value = newVillagerLoadingStates;
      }
    };
    const toggleAllVillagers = () => {
      villagerShowDetailsAll.value = !villagerShowDetailsAll.value;
      localStorage.setItem(`villager_details_all_${props.map.id}`, villagerShowDetailsAll.value.toString());
    };
    const sortVillagers = () => {
      editedMap.value.villagers.sort((a, b) => (a.chance || 0) - (b.chance || 0));
      const newVillagerLoadingStates = {};
      editedMap.value.villagers.forEach((_, index) => {
        newVillagerLoadingStates[index] = villagerLoadingStates.value[index] || false;
      });
      villagerLoadingStates.value = newVillagerLoadingStates;
    };
    const addReward = () => {
      const index = editedMap.value.rewards.length;
      editedMap.value.rewards.push({ itemName: '', dropChance: 0 });
      loadingStates.value[index] = false;
    };
    const removeReward = (index) => {
      editedMap.value.rewards.splice(index, 1);
      delete loadingStates.value[index];
      const newLoadingStates = {};
      Object.keys(loadingStates.value).forEach((key) => {
        const newKey = parseInt(key) > index ? parseInt(key) - 1 : parseInt(key);
        newLoadingStates[newKey] = loadingStates.value[key];
      });
      loadingStates.value = newLoadingStates;
    };
    const toggleAllRewards = () => {
      rewardShowDetailsAll.value = !rewardShowDetailsAll.value;
      localStorage.setItem(`reward_details_all_${props.map.id}`, rewardShowDetailsAll.value.toString());
    };
    const sortRewards = () => {
      editedMap.value.rewards.sort((a, b) => (a.dropChance || 0) - (b.dropChance || 0));
      const newLoadingStates = {};
      editedMap.value.rewards.forEach((_, index) => {
        newLoadingStates[index] = loadingStates.value[index] || false;
      });
      loadingStates.value = newLoadingStates;
    };
    const addSituation = () => {
      const index = editedMap.value.situations.length;
      editedMap.value.situations.push({
        name: '',
        minZombies: 1,
        maxZombies: 1,
        chance: 0,
        rewards: [{ itemName: '', dropChance: 0 }],
      });
    };
    const removeSituation = (index) => {
      if (editedMap.value.situations.length > 1) {
        editedMap.value.situations.splice(index, 1);
      }
    };
    const addSituationReward = (situationIndex) => {
      const rewardIndex = editedMap.value.situations[situationIndex].rewards.length;
      editedMap.value.situations[situationIndex].rewards.push({ itemName: '', dropChance: 0 });
    };
    const removeSituationReward = (situationIndex, rewardIndex) => {
      if (editedMap.value.situations[situationIndex].rewards.length > 1) {
        editedMap.value.situations[situationIndex].rewards.splice(rewardIndex, 1);
      }
    };
    const toggleActive = () => {
      editedMap.value.isActive = !editedMap.value.isActive;
    };
    const toggleMerchantEnabled = () => {
      editedMap.value.isMerchantEnabled = !editedMap.value.isMerchantEnabled;
    };
    const toggleVillagerEnabled = () => {
      editedMap.value.isVillagerEnabled = !editedMap.value.isVillagerEnabled;
    };
    const toggleMutantEnabled = () => {
      editedMap.value.isMutantEnabled = !editedMap.value.isMutantEnabled;
    };
    const toggleOccupationAllowed = () => {
      editedMap.value.isOccupationAllowed = !editedMap.value.isOccupationAllowed;
    };
    const toggleLootingAllowed = () => {
      editedMap.value.isLootingAllowed = !editedMap.value.isLootingAllowed;
    };
    const fetchItemDetails = async (itemName, index) => {
      if (!itemName) {
        delete itemDetails.value[itemName];
        loadingStates.value[index] = false;
        return;
      }
      loadingStates.value[index] = true;
      try {
        itemDetails.value[itemName] = gameItemsData[itemName] || null;
      } catch (error) {
        console.error(`Error fetching item ${itemName}:`, error);
        itemDetails.value[itemName] = null;
      } finally {
        loadingStates.value[index] = false;
      }
    };
    const fetchZombieDetails = async (zombieName, index) => {
      if (!zombieName) {
        delete zombieDetails.value[zombieName];
        zombieLoadingStates.value[index] = false;
        return;
      }
      zombieLoadingStates.value[index] = true;
      try {
        const zombieDoc = await projectFirestore.collection('gameZombies').doc(zombieName).get();
        zombieDetails.value[zombieName] = zombieDoc.exists ? zombieDoc.data() : null;
      } catch (error) {
        console.error(`Error fetching zombie ${zombieName}:`, error);
        zombieDetails.value[zombieName] = null;
      } finally {
        zombieLoadingStates.value[index] = false;
      }
    };
    const fetchMerchantDetails = async (merchantName, index) => {
      if (!merchantName) {
        delete merchantDetails.value[merchantName];
        merchantLoadingStates.value[index] = false;
        return;
      }
      merchantLoadingStates.value[index] = true;
      try {
        const merchantDoc = await projectFirestore.collection('gameMerchants').doc(merchantName).get();
        merchantDetails.value[merchantName] = merchantDoc.exists ? merchantDoc.data() : null;
      } catch (error) {
        console.error(`Error fetching merchant ${merchantName}:`, error);
        merchantDetails.value[merchantName] = null;
      } finally {
        merchantLoadingStates.value[index] = false;
      }
    };
    const fetchMutantDetails = async (mutantName, index) => {
      if (!mutantName) {
        delete mutantDetails.value[mutantName];
        mutantLoadingStates.value[index] = false;
        return;
      }
      mutantLoadingStates.value[index] = true;
      try {
        const mutantDoc = await projectFirestore.collection('gameMutants').doc(mutantName).get();
        mutantDetails.value[mutantName] = mutantDoc.exists ? mutantDoc.data() : null;
      } catch (error) {
        console.error(`Error fetching mutant ${mutantName}:`, error);
        mutantDetails.value[mutantName] = null;
      } finally {
        mutantLoadingStates.value[index] = false;
      }
    };
    const fetchVillagerDetails = async (villagerName, index) => {
      if (!villagerName) {
        delete villagerDetails.value[villagerName];
        villagerLoadingStates.value[index] = false;
        return;
      }
      villagerLoadingStates.value[index] = true;
      try {
        const villagerDoc = await projectFirestore.collection('gameVillagers').doc(villagerName).get();
        villagerDetails.value[villagerName] = villagerDoc.exists ? villagerDoc.data() : null;
      } catch (error) {
        console.error(`Error fetching villager ${villagerName}:`, error);
        villagerDetails.value[villagerName] = null;
      } finally {
        villagerLoadingStates.value[index] = false;
      }
    };
    const getStars = (rarity) => {
      const numStars = Number(rarity) || 0;
      return '★'.repeat(numStars);
    };
    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/40?text=Image+Not+Found';
    };
    watch(
      () => editedMap.value.rewards,
      (newRewards) => {
        if (Array.isArray(newRewards)) {
          newRewards.forEach((reward, index) => {
            if (reward.itemName && !itemDetails.value[reward.itemName] && !loadingStates.value[index]) {
              fetchItemDetails(reward.itemName, index);
            }
          });
        }
      },
      { deep: true }
    );
    watch(
      () => editedMap.value.zombies,
      (newZombies) => {
        if (Array.isArray(newZombies)) {
          newZombies.forEach((zombie, index) => {
            if (zombie.name && !zombieDetails.value[zombie.name] && !zombieLoadingStates.value[index]) {
              fetchZombieDetails(zombie.name, index);
            }
          });
        }
      },
      { deep: true }
    );
    watch(
      () => editedMap.value.merchants,
      (newMerchants) => {
        if (Array.isArray(newMerchants)) {
          newMerchants.forEach((merchant, index) => {
            if (merchant.name && !merchantDetails.value[merchant.name] && !merchantLoadingStates.value[index]) {
              fetchMerchantDetails(merchant.name, index);
            }
          });
        }
      },
      { deep: true }
    );
    watch(
      () => editedMap.value.mutants,
      (newMutants) => {
        if (Array.isArray(newMutants)) {
          newMutants.forEach((mutant, index) => {
            if (mutant.name && !mutantDetails.value[mutant.name] && !mutantLoadingStates.value[index]) {
              fetchMutantDetails(mutant.name, index);
            }
          });
        }
      },
      { deep: true }
    );
    watch(
      () => editedMap.value.villagers,
      (newVillagers) => {
        if (Array.isArray(newVillagers)) {
          newVillagers.forEach((villager, index) => {
            if (villager.name && !villagerDetails.value[villager.name] && !villagerLoadingStates.value[index]) {
              fetchVillagerDetails(villager.name, index);
            }
          });
        }
      },
      { deep: true }
    );
    const saveChanges = async () => {
      if (totalChance.value > 100) {
        alert('โอกาสการเจอซอมบี้รวมกันต้องไม่เกิน 100%');
        return;
      }
      if (editedMap.value.maxZombies < editedMap.minZombies) {
        alert('จำนวนซอมบี้สูงสุดต้องมากกว่าหรือเท่ากับจำนวนขั้นต่ำในแผนที่');
        return;
      }
      if (editedMap.value.diamondDropMax < editedMap.diamondDropMin) {
        alert('จำนวนเพชรสูงสุดต้องมากกว่าหรือเท่ากับจำนวนขั้นต่ำ');
        return;
      }
      if (hasInvalidSituations.value) {
        alert('จำนวนซอมบี้สูงสุดในสถานการณ์ต้องมากกว่าหรือเท่ากับจำนวนขั้นต่ำ');
        return;
      }
      try {
        const filteredRewards = (editedMap.value.rewards || []).filter((r) => r.itemName && r.dropChance > 0);
        const filteredZombies = (editedMap.value.zombies || []).filter((z) => z.name && z.chance > 0);
        const filteredMerchants = (editedMap.value.merchants || []).filter((m) => m.name && m.chance > 0);
        const filteredMutants = editedMap.value.isMutantEnabled ? (editedMap.value.mutants || []).filter((m) => m.name && m.chance > 0) : [];
        const filteredVillagers = (editedMap.value.villagers || []).filter((v) => v.name && v.chance > 0);
        const filteredSituations = (editedMap.value.situations || [])
          .filter((s) => s.name && s.chance > 0)
          .map((s) => ({
            name: s.name,
            minZombies: s.minZombies,
            maxZombies: s.maxZombies,
            chance: s.chance,
            rewards: (s.rewards || []).filter((r) => r.itemName && r.dropChance > 0),
          }));
        const data = {
          type: editedMap.value.type,
          name: editedMap.value.name,
          imageUrl: editedMap.value.imageUrl,
          requiredLevel: editedMap.value.requiredLevel,
          roomRequired: editedMap.value.roomRequired,
          requiredEnergy: editedMap.value.requiredEnergy,
          reduceHappy: editedMap.value.reduceHappy,
          requiredCoins: editedMap.value.requiredCoins,
          requiredGold: editedMap.value.requiredGold,
          requiredDiamonds: editedMap.value.requiredDiamonds,
          requiredCash: editedMap.value.requiredCash,
          requiredRenown: editedMap.value.requiredRenown,
          requiredMap: editedMap.value.requiredMap || null,
          allowedBuildings: editedMap.value.allowedBuildings || [],
          escapeAttempts: editedMap.value.escapeAttempts,
          escapeCoins: editedMap.value.escapeCoins,
          isActive: editedMap.value.isActive,
          isMerchantEnabled: editedMap.value.isMerchantEnabled,
          isMutantEnabled: editedMap.value.isMutantEnabled,
          isVillagerEnabled: editedMap.value.isVillagerEnabled,
          isLootingAllowed: editedMap.value.isLootingAllowed,
          minZombies: editedMap.value.minZombies,
          maxZombies: editedMap.value.maxZombies,
          zombies: filteredZombies.map((z) => ({ name: z.name, chance: z.chance || 0 })),
          zombieNames: filteredZombies.map((z) => z.name),
          merchants: filteredMerchants.map((m) => ({ name: m.name, chance: m.chance || 0 })),
          merchantNames: filteredMerchants.map((m) => m.name),
          mutants: filteredMutants.map((m) => ({ name: m.name, chance: m.chance || 0 })),
          mutantNames: filteredMutants.map((m) => m.name),
          villagers: filteredVillagers.map((v) => ({ name: v.name, chance: v.chance || 0 })),
          villagerNames: filteredVillagers.map((v) => v.name),
          rewards: filteredRewards,
          rewardItemNames: filteredRewards.map((r) => r.itemName),
          situations: filteredSituations,
          situationNames: filteredSituations.map((s) => s.name),
          diamondDropMin: editedMap.value.diamondDropMin,
          diamondDropMax: editedMap.value.diamondDropMax,
          diamondDropChance: editedMap.value.diamondDropChance,
          isOccupationAllowed: editedMap.value.isOccupationAllowed,
          occupierUid: editedMap.value.occupierUid || null,
          occupationScore: editedMap.value.occupationScore,
          challengeCostDiamonds: editedMap.value.challengeCostDiamonds,
          dailyDiamondReward: editedMap.value.dailyDiamondReward,
          encounterChance: editedMap.value.encounterChance,
        };
        if (editedMap.value.name !== props.map.id) {
          const newNameDoc = await projectFirestore.collection('maps').doc(editedMap.value.name).get();
          if (newNameDoc.exists) {
            alert('ชื่อแผนที่นี้มีอยู่แล้ว กรุณาใช้ชื่ออื่น');
            return;
          }
          await projectFirestore.collection('maps').doc(editedMap.value.name).set(data);
          await projectFirestore.collection('maps').doc(props.map.id).delete();
          localStorage.removeItem(`zombie_details_all_${props.map.id}`);
          localStorage.removeItem(`merchant_details_all_${props.map.id}`);
          localStorage.removeItem(`mutant_details_all_${props.map.id}`);
          localStorage.removeItem(`villager_details_all_${props.map.id}`);
          localStorage.removeItem(`reward_details_all_${props.map.id}`);
        } else {
          await projectFirestore.collection('maps').doc(props.map.id).update(data);
        }
        emit('mapUpdated');
        emit('cancelModal');
      } catch (error) {
        console.error('Error updating map:', error);
        alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
      }
    };
    const deleteMap = async () => {
      if (confirm(`คุณแน่ใจหรือไม่ว่าต้องการลบแผนที่ "${editedMap.value.name || 'ไม่มีชื่อ'}"? การกระทำนี้ไม่สามารถย้อนกลับได้`)) {
        try {
          localStorage.removeItem(`zombie_details_all_${props.map.id}`);
          localStorage.removeItem(`merchant_details_all_${props.map.id}`);
          localStorage.removeItem(`mutant_details_all_${props.map.id}`);
          localStorage.removeItem(`villager_details_all_${props.map.id}`);
          localStorage.removeItem(`reward_details_all_${props.map.id}`);
          await projectFirestore.collection('maps').doc(props.map.id).delete();
          emit('mapDeleted');
          emit('cancelModal');
        } catch (error) {
          console.error('Error deleting map:', error);
        }
      }
    };
    const scrollToTop = () => {
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    const handleScroll = () => {
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        showBackToTop.value = scrollableDiv.scrollTop > 300;
      }
    };
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        emit('cancelModal');
      } else if (event.key === 'Enter' && !event.target.matches('input, textarea, button')) {
        saveChanges();
      }
    };
    onMounted(async () => {
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.addEventListener('scroll', handleScroll);
      }
      window.addEventListener('keydown', handleKeydown);
      // Defensive initialization with checks
      if (Array.isArray(editedMap.value.rewards)) {
        editedMap.value.rewards.forEach((_, index) => {
          loadingStates.value[index] = false;
        });
      } else {
        console.warn('editedMap.value.rewards is not an array:', editedMap.value.rewards);
      }
      if (Array.isArray(editedMap.value.zombies)) {
        editedMap.value.zombies.forEach((_, index) => {
          zombieLoadingStates.value[index] = false;
        });
      } else {
        console.warn('editedMap.value.zombies is not an array:', editedMap.value.zombies);
      }
      if (Array.isArray(editedMap.value.merchants)) {
        editedMap.value.merchants.forEach((_, index) => {
          merchantLoadingStates.value[index] = false;
        });
      } else {
        console.warn('editedMap.value.merchants is not an array:', editedMap.value.merchants);
      }
      if (Array.isArray(editedMap.value.mutants)) {
        editedMap.value.mutants.forEach((_, index) => {
          mutantLoadingStates.value[index] = false;
        });
      } else {
        console.warn('editedMap.value.mutants is not an array:', editedMap.value.mutants);
      }
      if (Array.isArray(editedMap.value.villagers)) {
        editedMap.value.villagers.forEach((_, index) => {
          villagerLoadingStates.value[index] = false;
        });
      } else {
        console.warn('editedMap.value.villagers is not an array:', editedMap.value.villagers);
      }
      zombieShowDetailsAll.value = localStorage.getItem(`zombie_details_all_${props.map.id}`) === 'true' || false;
      merchantShowDetailsAll.value = localStorage.getItem(`merchant_details_all_${props.map.id}`) === 'true' || false;
      mutantShowDetailsAll.value = localStorage.getItem(`mutant_details_all_${props.map.id}`) === 'true' || false;
      villagerShowDetailsAll.value = localStorage.getItem(`villager_details_all_${props.map.id}`) === 'true' || false;
      rewardShowDetailsAll.value = localStorage.getItem(`reward_details_all_${props.map.id}`) === 'true' || false;
      await Promise.all([
        ...(Array.isArray(editedMap.value.rewards) ? editedMap.value.rewards.map(async (reward, index) => {
          if (reward.itemName) {
            await fetchItemDetails(reward.itemName, index);
          }
        }) : []),
        ...(Array.isArray(editedMap.value.zombies) ? editedMap.value.zombies.map(async (zombie, index) => {
          if (zombie.name) {
            await fetchZombieDetails(zombie.name, index);
          }
        }) : []),
        ...(Array.isArray(editedMap.value.merchants) ? editedMap.value.merchants.map(async (merchant, index) => {
          if (merchant.name) {
            await fetchMerchantDetails(merchant.name, index);
          }
        }) : []),
        ...(Array.isArray(editedMap.value.mutants) ? editedMap.value.mutants.map(async (mutant, index) => {
          if (mutant.name) {
            await fetchMutantDetails(mutant.name, index);
          }
        }) : []),
        ...(Array.isArray(editedMap.value.villagers) ? editedMap.value.villagers.map(async (villager, index) => {
          if (villager.name) {
            await fetchVillagerDetails(villager.name, index);
          }
        }) : []),
      ]);
    });
    onUnmounted(() => {
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('keydown', handleKeydown);
    });
    return {
      editedMap,
      totalChance,
      remainingChance,
      hasInvalidSituations,
      wearableItemsCount,
      generalItemsCount,
      showBackToTop,
      tabs,
      currentTab,
      switchTab,
      addZombie,
      removeZombie,
      toggleAllZombies,
      normalizeChances,
      addMerchant,
      removeMerchant,
      toggleAllMerchants,
      sortMerchants,
      totalDesiredMutantChance,
      addMutant,
      removeMutant,
      toggleAllMutants,
      sortMutants,
      calculateMutantChances,
      addVillager,
      removeVillager,
      toggleAllVillagers,
      sortVillagers,
      addReward,
      removeReward,
      toggleAllRewards,
      sortRewards,
      addSituation,
      removeSituation,
      addSituationReward,
      removeSituationReward,
      toggleActive,
      toggleMerchantEnabled,
      toggleVillagerEnabled,
      toggleMutantEnabled,
      toggleOccupationAllowed,
      toggleLootingAllowed,
      saveChanges,
      deleteMap,
      scrollToTop,
      itemDetails,
      loadingStates,
      zombieDetails,
      zombieLoadingStates,
      zombieShowDetailsAll,
      merchantDetails,
      merchantLoadingStates,
      merchantShowDetailsAll,
      mutantDetails,
      mutantLoadingStates,
      mutantShowDetailsAll,
      villagerDetails,
      villagerLoadingStates,
      villagerShowDetailsAll,
      rewardShowDetailsAll,
      getStars,
      handleImageError,
      selectedItemType,
      filteredRewards,
    };
  },
};
</script>
<style scoped>
.animate-fade-in {
  animation: fadeIn 0.3s ease-in;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.scroll-smooth {
  scroll-behavior: smooth;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: rgba(79, 70, 229, 0.5) rgba(31, 41, 55, 0.5);
}
.scrollbar-thin::-webkit-scrollbar {
  width: 8px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: rgba(31, 41, 55, 0.5);
  border-radius: 4px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgba(79, 70, 229, 0.5);
  border-radius: 4px;
  border: 2px solid rgba(31, 41, 55, 0.5);
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: rgba(79, 70, 229, 0.8);
}
button:hover,
.cursor-pointer:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease, background-color 0.2s ease, background-color 0.2s ease;
}
.bg-glass {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6366f1;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>